import { Button } from '@askable/ui/core/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@askable/ui/core/dialog';
import { toast } from '@askable/ui/core/sonner';
import * as _ from 'lodash';
import momentTimezone from 'moment-timezone';
import { useEffect, Fragment, useState } from 'react';
import { getBrowserTimezone } from 'shared-utils';
import { useMutation } from 'urql';

import { LoadingOverlay } from 'components/common';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { RevokeParticipantInvitation } from 'data/mutations/bookingSubmission/revokeParticipantInvitation.mutation';
import { bookingUtils } from 'lib/booking';
import { getCombinedErrorMessage } from 'utils/error_utils';

import RevokeInvitationConfirmationMessage from './revokeInvitationConfirmationMessage';
import RevokeInvitationContentContainer from './revokeInvitationContentContainer';

function RevokeInvitationDialog(props: any) {
  const [state, setState] = useState({
    loading: false,
    sms_message: '',
    showConfirmationMessage: false,
  });
  const { details } = useConnectedClient();

  const [, revokeParticipantInvitation] = useMutation(RevokeParticipantInvitation);

  useEffect(() => {
    // Only initialise state on modal [open], not when this hook is also called on modal [close].
    if (props.open) {
      const { booking, singleSession } = props;
      const timezone = _.get(booking, 'config.timezone') || getBrowserTimezone();
      const sessionStart = momentTimezone(singleSession.session.start).tz(timezone);
      const firstName = singleSession.participant.user.meta.identity.firstname;
      let clientName = '';

      if (details?.meta?.identity?.firstname) {
        clientName = details?.meta?.identity?.firstname;
      }
      // Initialise SMS message/Textarea value
      let smsMessage = `Hi ${firstName}, unfortunately we've had to cancel your invitation for the ${sessionStart.format('h:mmA')} session on the ${sessionStart.format(
        'Do MMMM',
      )} in ${booking.config.location.city} that you applied for. Apologies for any inconvenience this may have caused.\n - ${clientName}`;
      if (bookingUtils.isRemote(booking)) {
        smsMessage = `Hi ${firstName}, unfortunately we've had to cancel your invitation for the ${sessionStart.format('h:mmA')} session on the ${sessionStart.format(
          'Do MMMM',
        )} that you applied for. Apologies for any inconvenience this may have caused.\n - ${clientName}`;
      } else if (bookingUtils.isOnlineTask(booking) || bookingUtils.isLongitudinal(booking)) {
        smsMessage = `Hi ${firstName}, unfortunately we've had to cancel your invitation for the ${
          bookingUtils.isOnlineTask(booking) ? 'online task' : 'research'
        } you applied for. Apologies for any inconvenience this may have caused.\n - ${clientName}`;
      }
      setState({
        ...state,
        sms_message: smsMessage,
        showConfirmationMessage: false,
      });
    }
  }, [props.open]);

  // Handler for revoke button click
  const revokeInvitation = async () => {
    setState({ ...state, loading: true });

    const { sms_message } = state;
    const { singleSession } = props;
    const booking_participant_id = singleSession.participant.invitedSession._id;

    // Call Revoke Mutation
    const result = await revokeParticipantInvitation({
      participant_session_id: booking_participant_id,
      sms_message,
    });
    if (result.error) {
      toast.error(getCombinedErrorMessage(result.error, 'Error saving study'));
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ showConfirmationMessage: true;... Remove this comment to see the full error message
    setState({ showConfirmationMessage: true, loading: false });
  };

  // Used for any cleanup prior to modal close
  const handleClose = () => {
    props.onClose();
  };

  // Keeps SMS message content in sync with TextArea value
  const updateSmsMessage = (msg: any) => {
    setState({
      ...state,
      sms_message: msg,
    });
  };

  const { loading, sms_message, showConfirmationMessage } = state;
  const { open, singleSession } = props;
  const { participant, session } = singleSession;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && handleClose()}>
      <DialogContent className="revokeInvitationContentContainer dialogMaxWidth" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="sr-only">Revoke invitation</DialogTitle>
        </DialogHeader>
        <div className="dialogBody text-pretty text-sm">
          {showConfirmationMessage ? (
            <RevokeInvitationConfirmationMessage {...props} showConfirmationMessage onFinishCustom={handleClose} />
          ) : (
            <Fragment>
              {loading && <LoadingOverlay style={{ opacity: 0.8 }} />}
              <p>Revoke {_.get(participant, 'user.meta.identity.firstname')}&apos;s invite for this session?</p>
              <RevokeInvitationContentContainer
                participant={participant}
                session={session}
                onSmsMessageChange={(msg: any) => {
                  updateSmsMessage(msg);
                }}
                smsMessage={sms_message}
                {...props}
              />
              <div className="buttonsContainer">
                <Button
                  variant="default"
                  size="default"
                  className="revokeInviteBtn"
                  onClick={() => void revokeInvitation()}
                >
                  Revoke Invite
                </Button>
              </div>
            </Fragment>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RevokeInvitationDialog;
